import { useEffect, useState } from "react";
import { Modal, Checkbox, Input } from "antd";
import { Trans } from "react-i18next";
import { t } from "i18next";
import { CommonButton } from "@components/CommonButton";
import { fetchAssetDownloaded, fetchRefundQuota, submitReport } from "@apis/http/api";
import message from "@utils/message";
import Section from "@components/Section";
import { IconClose } from "@icons/index";

const ISSUE_OPTIONS = [
  { value: "visual_distortion", label: "Visual distortion/Blank screen" },
  { value: "audio_sync", label: "Audio sync issues" },
  { value: "garbled_text", label: "Garbled text" },
  { value: "other", label: "Other" },
];

interface ReportIssueModalProps {
  open: boolean;
  onClose: () => void;
  assetId?: number;
  jobType?: string;
  onSubmitted?: () => void;
}

const ReportIssueModal = ({
  open,
  onClose,
  assetId,
  jobType = "VIDEO_CREATION",
  onSubmitted,
}: ReportIssueModalProps) => {
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [otherText, setOtherText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [quotaTotal, setQuotaTotal] = useState<number>(0);
  const [quotaRemaining, setQuotaRemaining] = useState<number>(0);
  const [quotaLoading, setQuotaLoading] = useState(true);
  const [downloaded, setDownloaded] = useState(false);

  const fetchQuotaAndDownloaded = async () => {
    setQuotaLoading(true);
    // 查询问题报告退款次数配额
    const quotaReq = fetchRefundQuota({ jobType: "VIDEO_CREATION" } as any)
      .then((res: any) => {
        const data = res?.data ?? res;
        setQuotaTotal(data?.total ?? 0);
        setQuotaRemaining(data?.remaining ?? 0);
      })
      .catch(() => {
        setQuotaTotal(0);
        setQuotaRemaining(0);
      });
    // 查询资产是否已下载
    const downloadedReq = assetId
      ? fetchAssetDownloaded({ assetId } as any)
          .then((res: any) => setDownloaded(!!(res?.data ?? res)))
          .catch(() => setDownloaded(false))
      : Promise.resolve();
    Promise.all([quotaReq, downloadedReq]).finally(() => setQuotaLoading(false));
  };

  useEffect(() => {
    if (!open || !assetId) return;
    fetchQuotaAndDownloaded();
  }, [open, assetId]);

  const hasOther = selectedReasons.includes("other");
  const hasRefund = !downloaded && quotaRemaining > 0;

  const canSubmit = !quotaLoading && selectedReasons.length > 0 && (!hasOther || otherText.trim().length > 0);

  const handleSubmit = async () => {
    if (!canSubmit || assetId == null) return;
    setSubmitting(true);
    try {
      const res: any = await submitReport({
        jobType,
        assetId,
        feedback: {
          issueTypes: selectedReasons.filter((r) => r !== "other"),
          otherDescription: hasOther ? otherText.trim() : undefined,
        },
      } as any);
      message.success(t(res?.message || "Feedback sent. Thanks for making us better!"));
      if (hasRefund) {
        onSubmitted?.();
      }
      handleClose();
    } catch (error: any) {
      message.error(t(error.message || "Feedback already received. No need to resubmit, our team is looking into it."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedReasons([]);
    setOtherText("");
    onClose();
  };

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  if (!open) return null;

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      keyboard={false}
      footer={
        <div className="flex justify-end gap-[8px]">
          <CommonButton onClick={handleClose}>
            <Trans>Cancel</Trans>
          </CommonButton>
          <CommonButton type="primary" disabled={!canSubmit} loading={submitting} onClick={handleSubmit}>
            <Trans>Submit</Trans>
          </CommonButton>
        </div>
      }
      destroyOnHidden
      closeIcon={<IconClose className="w-[20px] h-[20px]" />}
      width={450}
    >
      <Section status={quotaLoading ? "loading" : "succeeded"} className="border-0! min-h-[230px]">
        <div className="text-kds-fg font-semibold text-[16px] mb-[16px]">
          {hasRefund ? t("Report Issue") : t("Provide Feedback")}
        </div>
        <div className="bg-kds-bg-subtle px-[24px] py-[12px] text-kds-fg text-[14px] mb-[16px] -mx-[24px] font-normal leading-[20px]">
          {hasRefund ? (
            <Trans
              i18nKey="report_issue_refund_hint"
              defaults="Upon approval, your credits will be refunded and this video will be deleted. Remaining refund requests this month: <1>{{remaining}}</1>/{{total}}."
              values={{ remaining: quotaRemaining, total: quotaTotal }}
              components={{ 1: <span className="text-kds-accent" /> }}
            />
          ) : (
            <Trans
              i18nKey="report_issue_no_refund_hint"
              defaults="Your feedback helps us improve the AI model to generate better videos."
              components={{ 1: <span className="text-kds-danger-fg" /> }}
            />
          )}
        </div>

        <div className="text-kds-fg font-medium text-[14px] mb-[10px]">
          <Trans>What went wrong with this video?</Trans>
        </div>

        <Checkbox.Group
          className="flex flex-col gap-[10px]"
          value={selectedReasons}
          onChange={(values) => setSelectedReasons(values as string[])}
        >
          {ISSUE_OPTIONS.map((option) => (
            <Checkbox key={option.value} value={option.value}>
              <Trans>{option.label}</Trans>
            </Checkbox>
          ))}
        </Checkbox.Group>

        {hasOther && (
          <div className="mt-[10px] mb-[30px]">
            <Input.TextArea
              autoSize={{ minRows: 1, maxRows: 5 }}
              value={otherText}
              showCount
              maxLength={200}
              onChange={(e) => setOtherText(e.target.value)}
              placeholder={t("Describe the issue here...")}
              style={{
                border: "1px solid var(--kds-border-solid)",
                borderRadius: "8px",
                padding: "9px 0",
                fontSize: "14px",
                lineHeight: "20px",
              }}
            />
          </div>
        )}
        <div className="border-b border-kds-border mt-[16px] -mx-[24px]"></div>
      </Section>
    </Modal>
  );
};

export default ReportIssueModal;
